import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Grid, Button, Card, CardContent, Switch } from '@mui/material';
import { Page, Header } from 'components';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SiteTrackerDatatable from './components/SiteTrackerDatatable';
import SiteTrackerDialog from './components/SiteTrackerDialog/SiteTrackerDialog';
import SiteTrackerDeleteDialog from './components/SiteTrackerDialog/SiteTrackerDeleteDialog';
import SiteTrackerAddFilesDialog from './components/SiteTrackerDialog/components/Teams/components/TeamsAddFilesDialog';
import TeamsDeleteFileDialog from './components/SiteTrackerDialog/components/Teams/components/TeamsDeleteFileDialog';
import SiteTrackerOptionsDialog from './components/SiteTrackerOptionsDialog/SiteTrackerOptionsDialog';
import SiteTrackerFileUpdateSitesDialog from './components/SiteTrackerFileUpdateSitesDialog';
import SiteTrackerImportValidationsDialog from './components/SiteTrackerImportValidationsDialog';
import SiteTrackerUpdateDialog from './components/SiteTrackerUpdate/SiteTrackerUpdateDialog';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import SettingsIcon from '@mui/icons-material/Settings';

import {
	SetFormState,
	GetSites,
	ExportSitesAll,
	ExportSitesByType,
	ExportSitesVMO2,
	ToggleSiteTrackerDialog,
	ToggleDeleteSiteTrackerDialog,
	ToggleAttachFileSiteTrackerDialog,
	ToggleDeleteAttachmentDialog,
	ToggleOptionsSiteTrackerDialog,
	ToggleSiteTrackerFileUpdateSitesDialog,
	ToggleSiteTrackerImportValidationsDialog,
	ToggleSiteTrackerUpdateDialog,
	ChangeColumnVisibility, GetVisibleColumns,
	ChangeFilterVisibility, GetVisibleFilters,
} from '../actions/SiteTrackerActions';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(1),
	},
	tabs: {
		marginTop: theme.spacing(1),
	},
	content: {
		marginTop: theme.spacing(1),
	},
}));

const SiteTracker = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.siteTrackerReducer.formState);
	const userId = useSelector(state => state.session.user.id);
	const role = useSelector(state => state.session.user.role);
	const sites = useSelector((state) => state.siteTrackerReducer.sites);
	const siteTrackerDialogVisibility = useSelector((state) => state.siteTrackerReducer.SiteTrackerDialogVisibility);
	const deleteSiteTrackerDialogVisibility = useSelector((state) => state.siteTrackerReducer.deleteSiteTrackerDialogVisibility);
	const attachFileSiteTrackerDialogVisibility = useSelector((state) => state.siteTrackerReducer.attachFileSiteTrackerDialogVisibility);
	const attachFileSiteTrackerDialogTableId = useSelector((state) => state.siteTrackerReducer.attachFileSiteTrackerDialogTableId);
	const attachFileSiteTrackerDialogTitle = useSelector((state) => state.siteTrackerReducer.attachFileSiteTrackerDialogTitle);
	const deleteAttachmentDialogVisibility = useSelector((state) => state.siteTrackerReducer.deleteAttachmentDialogVisibility);
	const deleteAttachmentDialogTableId = useSelector((state) => state.siteTrackerReducer.deleteAttachmentDialogTableId);
	const deleteAttachmentDialogId = useSelector((state) => state.siteTrackerReducer.deleteAttachmentDialogId);
	const optionsSiteTrackerDialogVisibility = useSelector((state) => state.siteTrackerReducer.optionsSiteTrackerDialogVisibility);
	const fileUpdateSitesSiteTrackerDialogVisibility = useSelector((state) => state.siteTrackerReducer.fileUpdateSitesSiteTrackerDialogVisibility);
	const importValidationsSiteTrackerDialogVisibility = useSelector((state) => state.siteTrackerReducer.importValidationsSiteTrackerDialogVisibility);
	const updateSiteTrackerDialogVisibility = useSelector((state) => state.siteTrackerReducer.updateSiteTrackerDialogVisibility);
	const visibleColumns = useSelector(state => state.siteTrackerReducer.visibleColumns);
	const visibleFilters = useSelector(state => state.siteTrackerReducer.visibleFilters);

	const tableName = 'SiteTrackerTable';

	useEffect(() => {
		if (siteTrackerDialogVisibility === false && deleteSiteTrackerDialogVisibility === false) {
			dispatch(GetSites(userId, role));
			dispatch(GetVisibleColumns(userId, tableName));
			dispatch(GetVisibleFilters(userId, tableName));
		}
	}, [siteTrackerDialogVisibility, deleteSiteTrackerDialogVisibility]);

	const _siteTrackerDialogClose = (event, reason) => {
		if (reason !== 'backdropClick') {
			dispatch(ToggleSiteTrackerDialog(false));
		}
	};

	const _siteTrackerDeleteDialogClose = () => {
		dispatch(ToggleDeleteSiteTrackerDialog(false));
	};

	const _attachFileSiteTrackerDialogClose = () => {
		dispatch(ToggleAttachFileSiteTrackerDialog(false));
	};

	const _removeFileSiteTrackerDialogClose = () => {
		dispatch(ToggleDeleteAttachmentDialog(false));
	};

	const _optionsSiteTrackerDialogClose = () => {
		dispatch(ToggleOptionsSiteTrackerDialog(false));
	};

	const _onFileUpdateSitesClose = () => {
		dispatch(ToggleSiteTrackerFileUpdateSitesDialog(false));
	};

	const _onImportValidationsClose = () => {
		dispatch(ToggleSiteTrackerImportValidationsDialog(false));
	};

	const _onUpdateSitesClose = () => {
		dispatch(ToggleSiteTrackerUpdateDialog(false));
	};

	const _onCreateSiteSiteClick = () => {
		dispatch(ToggleSiteTrackerDialog(true));
	};

	const _onExportSitesAllClick = () => {
		dispatch(ExportSitesAll(userId, role));
	};

	const _onExportSitesByTypeClick = () => {
		dispatch(ExportSitesByType(userId, role));
	};
	
	const _onExportSitesVMO2Click = () => {
		dispatch(ExportSitesVMO2(userId, role));
	}

	const _onFileUpdateSitesClick = () => {
		dispatch(ToggleSiteTrackerFileUpdateSitesDialog(true));
	};

	const _onImportValidationsClick = () => {
		dispatch(ToggleSiteTrackerImportValidationsDialog(true));
	};

	const _onUpdateSitesClick = () => {
		dispatch(ToggleSiteTrackerUpdateDialog(true));
	};

	const _onOptionsClick = () => {
		dispatch(ToggleOptionsSiteTrackerDialog(true));
	};

	const actions = [
		// { icon: <AddBoxIcon />, name: 'Create Site', id: 'createSite' },
		{ icon: <FileOpenIcon sx={{ color: 'green' }} />, name: 'Export Sites By Type', id: 'exportSitesByType' },
		{ icon: <FileOpenIcon sx={{ color: 'orange' }} />, name: 'Export Sites VMO2', id: 'exportSitesVMO2' },
		{ icon: <FileOpenIcon sx={{ color: 'red' }} />, name: 'Export Sites All', id: 'exportSitesAll' },
		// { icon: <FileOpenIcon sx={{ color: 'orange' }} />, name: 'Import Validations', id: 'importValidations' },

	];

	if (role === 'Administrator' || role === 'Manager') {
		actions.push({ icon: <InsertDriveFileIcon sx={{ color: 'blue' }} />, name: 'File Update Sites', id: 'fileUpdateSites' },);
		actions.push({ icon: <DynamicFeedIcon />, name: 'Update Sites', id: 'updateSites' },);
		actions.push({ icon: <ManageAccountsIcon />, name: 'Options', id: 'options' });
	}

	const _handleSpeedDialClick = (action) => {
		if (action.id === 'createSite') {
			_onCreateSiteSiteClick();
		} else if (action.id === 'importValidations') {
			_onImportValidationsClick();
		} else if (action.id === 'exportSitesAll') {
			_onExportSitesAllClick();
		} else if (action.id === 'exportSitesByType') {
			_onExportSitesByTypeClick();
		} else if (action.id === 'exportSitesVMO2') {
			_onExportSitesVMO2Click();
		} else if (action.id === 'fileUpdateSites') {
			_onFileUpdateSitesClick();
		} else if (action.id === 'updateSites') {
			_onUpdateSitesClick();
		} else if (action.id === 'options') {
			_onOptionsClick();
		}
	};

	const _changeColumnVisibility = (changeColumn) => {
		dispatch(ChangeColumnVisibility(changeColumn));
	};

	const _changeFilterVisibility = (columnName, filterValue) => {
		dispatch(ChangeFilterVisibility(columnName, filterValue));
	};

	return (
		<Page className={classes.root} title="Site Tracker">
			<Header module="Modules" view="Site Tracker" />
			<SpeedDial
				ariaLabel="SpeedDial"
				sx={{ position: 'absolute', bottom: 16, right: 16 }}
				icon={<SettingsIcon />}
			>
				{actions.map((action) => (
					<SpeedDialAction
						key={action.name}
						icon={action.icon}
						tooltipTitle={action.name}
						onClick={() => { _handleSpeedDialClick(action); }}
					/>
				))}
			</SpeedDial>
			<Grid className={classes.container} container spacing={1}>
				<Grid item lg={12} xs={12}>
					<SiteTrackerDatatable
						className={classes.results}
						data={sites}
						tableName={tableName}
						changeColumnVisibility={_changeColumnVisibility}
						visibleColumns={visibleColumns}
						changeFilterVisibility={_changeFilterVisibility}
						visibleFilters={visibleFilters}
						totalCount={sites.length || 0}
					/>
					{siteTrackerDialogVisibility && (
						<SiteTrackerDialog
							onClose={_siteTrackerDialogClose}
							open={siteTrackerDialogVisibility}
						/>
					)}
					{deleteSiteTrackerDialogVisibility && (
						<SiteTrackerDeleteDialog
							onClose={_siteTrackerDeleteDialogClose}
							open={deleteSiteTrackerDialogVisibility}
						/>
					)}
					{attachFileSiteTrackerDialogVisibility && (
						<SiteTrackerAddFilesDialog
							onClose={_attachFileSiteTrackerDialogClose}
							open={attachFileSiteTrackerDialogVisibility}
							tableId={attachFileSiteTrackerDialogTableId}
							title={attachFileSiteTrackerDialogTitle}
						/>
					)}
					{deleteAttachmentDialogVisibility && (
						<TeamsDeleteFileDialog
							onClose={_removeFileSiteTrackerDialogClose}
							open={deleteAttachmentDialogVisibility}
							tableId={deleteAttachmentDialogTableId}
							rowId={deleteAttachmentDialogId}
						/>
					)}
					{optionsSiteTrackerDialogVisibility && (
						<SiteTrackerOptionsDialog
							onClose={_optionsSiteTrackerDialogClose}
							open={optionsSiteTrackerDialogVisibility}
						/>
					)}
					{fileUpdateSitesSiteTrackerDialogVisibility && (
						<SiteTrackerFileUpdateSitesDialog
							onClose={_onFileUpdateSitesClose}
							open={fileUpdateSitesSiteTrackerDialogVisibility}
						/>
					)}
					{importValidationsSiteTrackerDialogVisibility && (
						<SiteTrackerImportValidationsDialog
							onClose={_onImportValidationsClose}
							open={importValidationsSiteTrackerDialogVisibility}
						/>
					)}
					{updateSiteTrackerDialogVisibility && (
						<SiteTrackerUpdateDialog
							onClose={_onUpdateSitesClose}
							open={updateSiteTrackerDialogVisibility}
						/>
					)}
				</Grid>
			</Grid>
		</Page>
	);
};

export default SiteTracker;
